import React, { lazy, Suspense, memo } from "react";

import { capitalize } from "src/utils";
import AdUnit from "src/components/AdUnit";
import NavComponent from "src/components/NavComponent";
import Search from "src/components/Search";
import SearchBar from "src/components/SearchBar";
import * as Placeholders from "src/component-placeholders";
import SponsoredApiAds from "src/components/SponsoredApiAds";
import SponsoredGcse from "src/components/SponsoredGcse";

const Fallback = () => null;

const suspend = (Comp, Placeholder) => props => (
  <Suspense fallback={Placeholder ? <Placeholder {...props} /> : <Fallback />}>
    <Comp {...props} />
  </Suspense>
);

const components = {
  // Not Chunked Components
  AdUnit,
  NavComponent,
  Search,
  SearchBar,
  SponsoredApiAds,
  SponsoredGcse,
  // Subpages
  Apps: suspend(lazy(() => import(/* webpackChunkName: "apps" */ "src/components/Apps"))),
  Social: suspend(lazy(() => import(/* webpackChunkName: "social" */ "src/components/Social"))),
  Local: suspend(lazy(() => import(/* webpackChunkName: "local" */ "src/components/Local"))),
  // Search: suspend(lazy(() => import(/* webpackChunkName: "search" */ "src/components/Search"))),
  PortalContentAI: suspend(lazy(() => import(/* webpackChunkName: "content" */ "src/components/PortalContentAI"))),

  // Chunked Components

  FeaturedAdTiles: suspend(
    lazy(() => import(/* webpackChunkName: "featured-ad-tiles" */ "src/components/FeaturedAdTiles")),
    Placeholders.FeaturedAdTiles
  ),
  Footer: suspend(
    lazy(() => import(/* webpackChunkName: "footer" */ "src/components/Footer")),
    Placeholders.Footer
  ),
  Horoscope: suspend(
    lazy(() => import(/* webpackChunkName: "horoscope" */ "src/components/Horoscope")),
    Placeholders.Horoscope
  ),
  News: suspend(
    lazy(() => import(/* webpackChunkName: "news" */ "src/components/News")),
    Placeholders.GenericFeed
  ),
  NewsFeed: suspend(
    lazy(() => import(/* webpackChunkName: "news-feed" */ "src/components/NewsFeed")),
    Placeholders.GenericFeed
  ),
  FeedBlock: suspend(
    lazy(() => import(/* webpackChunkName: "feed-block" */ "src/components/FeedBlock")),
    Placeholders.GenericFeed
  ),
  OriginalContent: suspend(
    lazy(() => import(/* webpackChunkName: "original-content" */ "src/components/OriginalContent")),
    Placeholders.GenericComponent
  ),

  AppSlider: suspend(
    lazy(() => import(/* webpackChunkName: "app-slider" */ "src/components/AppSlider")),
    Placeholders.AppSlider
  ),
  CustomComponent: suspend(lazy(() => import(/* webpackChunkName: "custom" */ "src/components/CustomComponent"))),
  CustomComponentV2: suspend(lazy(() => import(/* webpackChunkName: "custom" */ "src/components/CustomComponentV2"))),
  PushNotification: suspend(lazy(() => import(/* webpackChunkName: "push-notification" */ "src/components/PushNotification"))),

  BingTrendingTerms: suspend(
    lazy(() => import(/* webpackChunkName: "bing-trending-terms" */ "src/components/BingTrendingTerms")),
    Placeholders.Footer
  ),
  BingSyndicationJS: suspend(lazy(() => import(/* webpackChunkName: "bing-syndication-js" */ "src/components/BingSyndicationJS"))),
  Offers: suspend(
    lazy(() => import(/* webpackChunkName: "offers" */ "src/components/Offers")),
    Placeholders.AppSlider
  ),
  // SponsoredApiAds: suspend(lazy(() => import(/* webpackChunkName: "search" */ "src/components/SponsoredApiAds"))),
  PageOptions: suspend(lazy(() => import(/* webpackChunkName: "page-options" */ "src/components/PageOptions"))),
  GoogleCustomSearch: suspend(lazy(() => import(/* webpackChunkName: "google-custom-search" */ "src/components/GoogleCustomSearch"))),
  GoogleRelatedKeywords: suspend(lazy(() => import(/* webpackChunkName: "google-related-keywords" */ "src/components/GoogleRelatedKeywords"))),
  OrganicNewsResult: suspend(lazy(() => import(/* webpackChunkName: "organic-news-result" */ "src/components/OrganicNewsResult"))),
  // SponsoredGcse: suspend(lazy(() => import(/* webpackChunkName: "search" */ "src/components/SponsoredGcse"))),
  ArticlePage: suspend(lazy(() => import(/* webpackChunkName: "article_page" */ "src/components/ArticlePage"))),
  ContentAIRelatedNews: suspend(lazy(() => import(/* webpackChunkName: "article_page" */ "src/components/PortalContentAIArticle/ContentAIRelatedNews"))),
  BingRelatedKeywords: suspend(lazy(() => import(/* webpackChunkName: "google-related-keywords" */ "src/components/BingRelatedKeywords"))),
  RelatedKeywordsBlock: suspend(lazy(() => import(/* webpackChunkName: "related-keywords-block" */ "src/components/RelatedKeywordsBlock"))),
  PortalHomepageTemplate2Lazy: suspend(lazy(() => import(/* webpackChunkName: "PortalHomepage_Template_2" */ "src/components/PortalHomepage_Template_2"))),
  PortalHomepageTemplate3Lazy: suspend(lazy(() => import(/* webpackChunkName: "PortalHomepage_Template_2" */ "src/components/PortalHomepage_Template_3"))),
  PortalHomepageTemplate4Lazy: suspend(lazy(() => import(/* webpackChunkName: "PortalHomepage_Template_4" */ "src/components/PortalHomepage_Template_4"))),
  PortalHomepageTemplate5Lazy: suspend(lazy(() => import(/* webpackChunkName: "PortalHomepage_Template_4" */ "src/components/PortalHomepage_Template_5"))),
  Sitemap: suspend(lazy(() => import(/* webpackChunkName: "Sitemap" */ "src/components/Sitemap"))),
};

const loadComponent = name => components[capitalize(name)] || Fallback;

export const Load = memo(props => {
  const { name, ...rest } = props;
  const Component = loadComponent(name);

  return <Component {...rest} />;
});

export default loadComponent;
